import React, {useMemo, useState} from 'react';
import {Autocomplete, Box, Grid, styled, TextField, Typography,} from '@mui/material';
import moment from 'moment';
import {useGetAllQuery} from '../../../services/rtkQueryServices';
import {DateTimePicker, TimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';

const DateTimeStyled = styled(Box)(({theme}) => ({
    backgroundColor: theme.palette.error.main,
    width: '200px',
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.error.contrastText,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #CCC',
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

const DateTimeHoursContainerStyled = styled(Box)(({theme}) => ({
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.text.primary,
    width: '100%',
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 5,
}));

const HeaderContainerStyled = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
}));

function SessionReadOnly({data}: any) {
    const {title, description, venue, start, end, floorplan, speaker} = data;

    const {date, startTime, endTime} = useMemo(() => {
        return {
            date: moment(start).format('DD MMM YYYY'),
            startTime: moment(start).format('HH:mm A'),
            endTime: moment(end).format('HH:mm A'),
        };
    }, [start, end]);
    return (
        <Grid container gap={2}>
            <Grid item xs={12}>
                <HeaderContainerStyled>
                    <Typography variant='h3'>{speaker}</Typography>

                    <DateTimeStyled>
                        <Typography variant='button' sx={{fontWeight: 'bold'}}>
                            {date}
                        </Typography>
                        <DateTimeHoursContainerStyled>
                            <Typography>{startTime}</Typography>
                            <span>-</span>
                            <Typography>{endTime}</Typography>
                        </DateTimeHoursContainerStyled>
                    </DateTimeStyled>
                </HeaderContainerStyled>
            </Grid>
            <Grid item xs={12} sm={6}></Grid>
            <Grid item xs={12}>
                <Typography variant='h4'>{title}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='body1'>{description}</Typography>
            </Grid>
            <Grid item xs={12}>
                {venue}
            </Grid>
            <Grid item xs={12}>
                {floorplan}
            </Grid>
        </Grid>
    );
}

function SessionEdit({data, setPayload}: any) {
    const onFieldEdit = (e: any) => {
        setPayload({
            ...data,
            [e.target.name]: e.target.value,
        });
    };
    const {title, description, venue, start, end, floorplan, speaker} = data;
    const {date, startTime, endTime} = useMemo(() => {
        return {
            date: moment(start).format('DD MMM YYYY'),
            startTime: moment(start).format('HH:mm A'),
            endTime: moment(end).format('HH:mm A'),
        };
    }, [start, end]);
    const {
        data: apiData,
        isLoading,
        isError,
        isSuccess,
        isFetching,
        refetch,
    } = useGetAllQuery({
        resource: 'speakers',
        page: 0,
        search: '',
        pageSize: 1000,
    });

    const [datePicker, setDatePicker] = useState<any>();
    const speakers = useMemo(() => {
        if (apiData && apiData.result.length > 0) {
            return apiData.result.map((e: any) => ({
                id: e._id,
                label: `${e.name}`,
            }));
        }
        return [];
    }, []);


    return (
        <Box id='session-form' component={'form'} noValidate autoComplete='off'>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <DateTimePicker
                        label='Session Date'
                        defaultValue={dayjs(start)}
                        onChange={(newValue) => setPayload({...data, start: newValue})}
                        sx={{width: '100%'}}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <TimePicker
                        label='End Time'
                        sx={{width: '100%'}}
                        defaultValue={dayjs(end)}
                        onChange={(newValue) => setPayload({...data, end: newValue})}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        name='title'
                        label='Session Title'
                        defaultValue={title}
                        fullWidth
                        onBlur={onFieldEdit}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Autocomplete
                        disablePortal
                        id='speakerSearch'
                        options={speakers}
                        sx={{width: '100%'}}
                        renderInput={(inputParams) => (
                            <TextField
                                {...inputParams}
                                name='speaker'
                                label={'Speaker'}
                                fullWidth
                                onBlur={onFieldEdit}
                            />
                        )}
                        defaultValue={speaker}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label='Description'
                        name='description'
                        defaultValue={description}
                        multiline
                        minRows={5}
                        fullWidth
                        onBlur={onFieldEdit}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        name='venue'
                        label='Venue'
                        defaultValue={venue}
                        fullWidth
                        onBlur={onFieldEdit}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        name='floorplan'
                        label='Floor Plan'
                        defaultValue={floorplan}
                        fullWidth
                        onBlur={onFieldEdit}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export {SessionReadOnly, SessionEdit};
